import React from 'react';
import { Calendar, List, Clock, Grid, Download, Printer, Share2 } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

const ScheduleActions = ({ 
  activeView, 
  setActiveView,
  generatedSchedule,
  formData
}) => {
  // Функция для экспорта расписания в CSV
  const exportToCsv = () => {
    if (!generatedSchedule || !generatedSchedule.schedule || generatedSchedule.schedule.length === 0) {
      alert("Нет данных для экспорта.");
      return;
    }

    // Определяем заголовки
    const headers = ['Дата', 'День недели', 'Начало', 'Окончание', 'Предмет', 'Группа', 'Преподаватель', 'Аудитория'];

    // Дни недели на русском
    const daysOfWeek = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];

    // Преобразуем данные в строки CSV
    const rows = generatedSchedule.schedule.map(item => {
      const date = new Date(item.date);
      const formattedDate = date.toLocaleDateString('ru-RU');
      const dayOfWeek = daysOfWeek[date.getDay()];

      return [
        formattedDate,
        dayOfWeek,
        item.startTime,
        item.endTime,
        item.subjectName,
        item.groupName,
        item.teacherName,
        item.roomName
      ].join(';');
    });

    // Добавляем общую информацию в начало файла
    const infoRows = [
      ['Расписание', ''],
      ['Период:', `${new Date(generatedSchedule.startDate).toLocaleDateString('ru-RU')} - ${new Date(generatedSchedule.endDate).toLocaleDateString('ru-RU')}`],
      ['Всего дней:', generatedSchedule.meta.totalDays],
      ['Всего занятий:', generatedSchedule.meta.totalAssignments],
      ['Дата генерации:', new Date(generatedSchedule.meta.generatedAt).toLocaleString('ru-RU')],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расписание_занятий.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  
  // Функция для печати расписания
  const printSchedule = () => {
    const printContent = document.querySelector('.schedule-display');
    if (!printContent) {
      alert("Не удалось найти расписание для печати.");
      return;
    }
    
    const printWindow = window.open('', '_blank');
    
    if (!printWindow) {
      alert("Пожалуйста, разрешите всплывающие окна в вашем браузере.");
      return;
    }
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Расписание - Печать</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: #f2f2f2;
            }
            .header {
              margin-bottom: 20px;
            }
            .subject {
              font-weight: bold;
            }
            @media print {
              body {
                padding: 0;
              }
              button {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Расписание занятий</h1>
            <p>Период: ${new Date(generatedSchedule.startDate).toLocaleDateString('ru-RU')} - ${new Date(generatedSchedule.endDate).toLocaleDateString('ru-RU')}</p>
            <p>Учебное заведение: ${formData.scheduleType === 'school' ? 'Школа' : formData.scheduleType === 'university' ? 'Университет' : 'Учебное заведение'}</p>
            <button onclick="window.print()">Печать</button>
          </div>
    `);
    
    // Группируем расписание по датам
    const scheduleByDate = {};
    generatedSchedule.schedule.forEach(event => {
      const dateStr = new Date(event.date).toLocaleDateString('ru-RU');
      if (!scheduleByDate[dateStr]) {
        scheduleByDate[dateStr] = [];
      }
      scheduleByDate[dateStr].push(event);
    });
    
    // Сортируем даты
    const sortedDates = Object.keys(scheduleByDate).sort((a, b) => {
      return new Date(a.split('.').reverse().join('-')) - new Date(b.split('.').reverse().join('-'));
    });
    
    // Для каждой даты создаем таблицу с расписанием
    sortedDates.forEach(dateStr => {
      const events = scheduleByDate[dateStr];
      // Сортируем события по времени
      events.sort((a, b) => a.startMinutes - b.startMinutes);
      
      printWindow.document.write(`
        <h2>${dateStr} (${new Date(events[0].date).toLocaleDateString('ru-RU', {weekday: 'long'})})</h2>
        <table>
          <thead>
            <tr>
              <th>Время</th>
              <th>Предмет</th>
              <th>Группа</th>
              <th>Преподаватель</th>
              <th>Кабинет</th>
            </tr>
          </thead>
          <tbody>
      `);
      
      events.forEach(event => {
        printWindow.document.write(`
          <tr>
            <td>${event.startTime} - ${event.endTime}</td>
            <td class="subject">${event.subjectName}</td>
            <td>${event.groupName}</td>
            <td>${event.teacherName}</td>
            <td>${event.roomName}</td>
          </tr>
        `);
      });
      
      printWindow.document.write(`
          </tbody>
        </table>
      `);
    });
    
    printWindow.document.write(`
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };
  
  // Функция для поделиться расписанием
  const shareSchedule = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Расписание занятий',
        text: `Расписание на период ${new Date(generatedSchedule.startDate).toLocaleDateString('ru-RU')} - ${new Date(generatedSchedule.endDate).toLocaleDateString('ru-RU')}`,
        url: window.location.href
      })
      .catch(error => {
        console.error('Ошибка при попытке поделиться:', error);
      });
    } else {
      alert('Ваш браузер не поддерживает Web Share API. Пожалуйста, используйте экспорт в CSV для сохранения расписания.');
    }
  };
  
  return (
    <div className="mb-4">
      <div className="flex flex-wrap justify-between items-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={exportToCsv}
            icon={<Download className="w-4 h-4" />}
            variant="secondary"
            size="sm"
          >
            Экспорт CSV
          </Button>
          
          <Button
            type="button"
            onClick={printSchedule}
            icon={<Printer className="w-4 h-4" />}
            variant="secondary"
            size="sm"
          >
            Печать
          </Button>
          
          <Button
            type="button"
            onClick={shareSchedule}
            icon={<Share2 className="w-4 h-4" />}
            variant="secondary"
            size="sm"
          >
            Поделиться
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleActions;